<script>
  import {
    Popup,
    Page,
    Navbar,
    NavRight,
    Link,
    Button,
    Icon,
    f7,
  } from "framework7-svelte";
  import { BASE_URL } from "../services/service.config";
  import { formatPrice, formatFullTimestamp as formatTimestamp } from "../js/audit-utils";

  export let show = false;
  export let orderTag = null;
  export let environment = "LIVE";
  export let onClose = () => {};
  export let onInspectEvent = (event) => {};

  let loading = false;
  let timelineData = null;
  let error = null;

  $: if (show && orderTag) {
    loadTimeline(orderTag, environment);
  }

  async function loadTimeline(tag, env) {
    loading = true;
    error = null;
    timelineData = null;
    try {
      const res = await fetch(`${BASE_URL}/api/v1/audit/orders/${encodeURIComponent(tag)}/timeline?environment=${env}`);
      if (!res.ok) {
        throw new Error(`Failed to load order timeline (HTTP ${res.status})`);
      }
      const data = await res.json();
      timelineData = data;
    } catch (err) {
      console.error("Error loading order timeline:", err);
      error = err.message || "Failed to fetch order timeline";
    } finally {
      loading = false;
    }
  }

  function getTimeDeltaMs(curTs, prevTs) {
    if (!curTs || !prevTs) return 0;
    const t1 = new Date(curTs).getTime();
    const t2 = new Date(prevTs).getTime();
    if (isNaN(t1) || isNaN(t2)) return 0;
    return Math.max(0, t1 - t2);
  }

  function getStepIcon(type) {
    if (type.includes("FILL")) return "checkmark_alt_circle_fill";
    if (type.includes("REJECT") || type.includes("CANCEL")) return "xmark_circle_fill";
    if (type.includes("DISPATCH")) return "paperplane_fill";
    if (type.includes("ACK")) return "arrow_2_squarepath";
    if (type.includes("TRAILING")) return "slider_horizontal_3";
    if (type.includes("SIGNAL")) return "bolt_fill";
    return "circle_fill";
  }

  function getStepColor(type) {
    if (type.includes("FILL") || type.includes("PASSED")) return "#34d399";
    if (type.includes("REJECT") || type.includes("CANCEL")) return "#f87171";
    if (type.includes("SIGNAL")) return "#c084fc";
    if (type.includes("DISPATCH") || type.includes("ACK")) return "#60a5fa";
    if (type.includes("TRAILING")) return "#fb923c";
    return "#94a3b8";
  }
</script>

<Popup opened={show} onPopupClosed={onClose} class="audit-timeline-popup">
  <Page style="background: #0f172a; color: #e2e8f0;">
    <Navbar title="Order Lifecycle Timeline Trace">
      <NavRight>
        <Link popupClose iconF7="xmark_circle_fill" />
      </NavRight>
    </Navbar>

  <div class="timeline-container">
    {#if loading}
      <div class="loading-box">
        <div class="spinner"></div>
        <p>Tracing chronological lifecycle for order <code>{orderTag}</code>...</p>
      </div>
    {:else if error}
      <div class="error-box">
        <strong>⚠️ Error:</strong> {error}
        <Button small fill color="blue" class="margin-top" onClick={() => loadTimeline(orderTag, environment)}>
          Retry
        </Button>
      </div>
    {:else if timelineData}
      <!-- Order Summary Card -->
      <div class="summary-card">
        <div class="summary-top">
          <div>
            <span class="order-title"><code>{timelineData.order_tag || orderTag}</code></span>
            <span class="ucc-badge">Client: {timelineData.client_ucc || '-'}</span>
            <span class="symbol-badge">{timelineData.symbol || '-'}</span>
          </div>
          <div class="status-badge {timelineData.current_status === 'FILLED' ? 'bg-green' : timelineData.current_status === 'REJECTED' || timelineData.current_status === 'CANCELLED' ? 'bg-red' : 'bg-blue'}">
            {timelineData.current_status || 'IN_PROGRESS'}
          </div>
        </div>

        <div class="summary-metrics">
          <div class="m-box">
            <span class="m-k">Side / Leg</span>
            <span class="m-v">{timelineData.side || 'BUY'} (Leg {timelineData.leg_index !== undefined ? timelineData.leg_index : 0})</span>
          </div>
          <div class="m-box">
            <span class="m-k">Total Qty</span>
            <span class="m-v">{timelineData.order_qty || 0}</span>
          </div>
          <div class="m-box">
            <span class="m-k">Filled Qty</span>
            <span class="m-v text-green font-bold">{timelineData.cum_fill_qty || timelineData.fill_qty || 0}</span>
          </div>
          <div class="m-box">
            <span class="m-k">Average Price</span>
            <span class="m-v font-bold">{formatPrice(timelineData.fill_price)}</span>
          </div>
          <div class="m-box">
            <span class="m-k">Slippage</span>
            <span class="m-v {Number(timelineData.slippage_paise || 0) > 0 ? 'text-orange' : 'text-green'}">
              {timelineData.slippage_paise ? `${timelineData.slippage_paise} ps` : '0.00 ps'}
            </span>
          </div>
          <div class="m-box">
            <span class="m-k">Trailing SL</span>
            <span class="m-v text-orange">{formatPrice(timelineData.trailing_sl_price)}</span>
          </div>
        </div>
      </div>

      <!-- Chronological Vertical Timeline -->
      <div class="timeline-steps">
        {#if !timelineData.events || timelineData.events.length === 0}
          <div class="empty-notice">No lifecycle events recorded for this order tag.</div>
        {:else}
          {#each timelineData.events as evt, index}
            <div class="timeline-item">
              <div class="timeline-axis">
                <div class="step-dot" style="background: {getStepColor(evt.event_type)};">
                  <Icon f7={getStepIcon(evt.event_type)} size="12" />
                </div>
                {#if index < timelineData.events.length - 1}
                  <div class="step-connector"></div>
                {/if}
              </div>

              <div class="timeline-body">
                <div class="step-card">
                  <div class="step-header">
                    <div class="step-name-row">
                      <span class="step-tag" style="background: {getStepColor(evt.event_type)}22; color: {getStepColor(evt.event_type)}; border: 1px solid {getStepColor(evt.event_type)}66;">
                        {evt.event_type}
                      </span>
                      {#if evt.exchange_order_no}
                        <span class="exch-num">Exch ID: <code>{evt.exchange_order_no}</code></span>
                      {/if}
                    </div>
                    <div class="step-time">
                      <span>⏱ {formatTimestamp(evt.created_at || evt.timestamp)}</span>
                      {#if index > 0 && timelineData.events[index - 1]}
                        <!-- Time delta -->
                        <span class="step-delta text-slate">
                          (+{getTimeDeltaMs(evt.created_at || evt.timestamp, timelineData.events[index-1].created_at || timelineData.events[index-1].timestamp)}ms)
                        </span>
                      {/if}
                    </div>
                  </div>

                  {#if evt.trigger_reason}
                    <div class="step-reason">
                      {evt.trigger_reason}
                    </div>
                  {/if}

                  <div class="step-info-row">
                    {#if evt.fill_qty || evt.order_qty}
                      <span>Qty: <strong>{evt.fill_qty || evt.order_qty}</strong></span>
                    {/if}
                    {#if evt.fill_price !== null && evt.fill_price !== undefined || evt.order_price !== null && evt.order_price !== undefined}
                      <span>Price: <strong>{formatPrice(evt.fill_price ?? evt.order_price)}</strong></span>
                    {/if}
                    {#if evt.slippage_paise}
                      <span class="text-orange">Slippage: {evt.slippage_paise} ps</span>
                    {/if}
                    {#if evt.trailing_sl_price}
                      <span class="text-orange">New SL: {formatPrice(evt.trailing_sl_price)}</span>
                    {/if}
                    <Button small outline color="blue" onClick={() => onInspectEvent(evt)}>
                      Forensic Card
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          {/each}
        {/if}
      </div>
    {/if}
  </div>
  </Page>
</Popup>

<style>
  .audit-timeline-popup {
    --f7-popup-tablet-width: 860px;
    --f7-popup-tablet-height: 88vh;
  }
  .timeline-container {
    padding: 16px;
    background: #0f172a;
    color: #e2e8f0;
    min-height: calc(100vh - 60px);
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  }
  .loading-box {
    text-align: center;
    padding: 48px;
    color: #94a3b8;
  }
  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #334155;
    border-top-color: #38bdf8;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin: 0 auto 16px;
  }
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  .error-box {
    background: #7f1d1d;
    color: #fecaca;
    padding: 16px;
    border-radius: 8px;
    margin: 16px 0;
  }

  .summary-card {
    background: #1e293b;
    border: 1px solid #334155;
    border-radius: 8px;
    padding: 14px 18px;
    margin-bottom: 20px;
  }
  .summary-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
  }
  .order-title code {
    background: #0f172a;
    color: #38bdf8;
    padding: 3px 8px;
    border-radius: 4px;
    font-size: 14px;
    border: 1px solid #334155;
  }
  .ucc-badge, .symbol-badge {
    background: #334155;
    color: #f1f5f9;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 11px;
    margin-left: 6px;
  }
  .status-badge {
    padding: 3px 10px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: bold;
  }
  .bg-green { background: #065f46; color: #34d399; }
  .bg-red { background: #7f1d1d; color: #f87171; }
  .bg-blue { background: #1e3a8a; color: #60a5fa; }

  .summary-metrics {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
    gap: 8px;
  }
  .m-box {
    background: #0f172a;
    border: 1px solid #334155;
    border-radius: 6px;
    padding: 6px 10px;
    display: flex;
    flex-direction: column;
  }
  .m-k {
    font-size: 9px;
    color: #64748b;
    text-transform: uppercase;
    margin-bottom: 2px;
  }
  .m-v {
    font-size: 13px;
    color: #f8fafc;
  }

  .timeline-steps {
    display: flex;
    flex-direction: column;
    padding-left: 10px;
  }
  .timeline-item {
    display: flex;
    position: relative;
  }
  .timeline-axis {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-right: 16px;
    width: 24px;
  }
  .step-dot {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #0f172a;
    z-index: 2;
  }
  .step-connector {
    width: 2px;
    flex: 1;
    background: #334155;
    margin: 4px 0;
  }
  .timeline-body {
    flex: 1;
    padding-bottom: 16px;
  }
  .step-card {
    background: #1e293b;
    border: 1px solid #334155;
    border-radius: 8px;
    padding: 10px 14px;
  }
  .step-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 6px;
  }
  .step-name-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .step-tag {
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 700;
  }
  .exch-num code {
    background: #0f172a;
    color: #38bdf8;
    padding: 1px 4px;
    border-radius: 3px;
    font-size: 11px;
  }
  .step-time {
    font-size: 11px;
    color: #94a3b8;
    display: flex;
    gap: 6px;
  }
  .step-delta {
    color: #64748b;
  }
  .step-reason {
    font-size: 12px;
    color: #cbd5e1;
    background: #0f172a;
    padding: 6px 10px;
    border-radius: 4px;
    margin-bottom: 8px;
  }
  .step-info-row {
    display: flex;
    align-items: center;
    gap: 16px;
    font-size: 12px;
    color: #94a3b8;
  }

  .empty-notice {
    color: #64748b;
    font-size: 12px;
    font-style: italic;
    padding: 16px 0;
  }

  .text-green { color: #34d399 !important; }
  .text-orange { color: #fb923c !important; }
  .text-slate { color: #94a3b8 !important; }
  .font-bold { font-weight: bold; }
</style>
